/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { UpdateActions } from '../actions/universalTrackerValue';
import { NotApplicableTypes, StatusReadableText, UtrvStatus } from '../constants/status';
import { convertValue, getUnitDescription, UnitTypes } from './units';
import NumberFormat from './number-format';
import React from 'react';
import SimpleTooltip from '../components/simple-tooltip';
import { UniversalTrackerValuePlain, ValueData } from '../types/surveyScope';
import { TableValueDataData } from '../components/survey/form/input/table/InputInterface';
import UniversalTrackerValue from '../model/UniversalTrackerValue';
import UniversalTracker from '../model/UniversalTracker';
import { ConversionData, UtrvAssuranceStatus, ValueHistory } from '../types/universalTrackerValue';
import { SurveyModelMinimalUtrv, UnitConfig } from '../model/surveyData';
import { UniversalTrackerPlain, UtrValueType } from '../types/universalTracker';
import { DATE, formatDate } from './date';
import { QUESTION } from '../constants/terminology';
import { isNumeric } from './number';

type MinimalValueHistory = Pick<ValueHistory, 'userId' | 'date'>;
export const userHistoryComparator = (a: MinimalValueHistory, b: MinimalValueHistory): number => {
  if (a.date > b.date) return -1;
  if (a.date < b.date) return 1;
  return 0;
}

function getHistory(utrv: { history?: MinimalValueHistory[] }): MinimalValueHistory[] {
  const history = utrv.history;
  if (!Array.isArray(history)) {
    return [];
  }
  history.sort(userHistoryComparator);
  return history;
}

function getLastHistoryUserId(utrv: { history?: MinimalValueHistory[] }): string {
  const history = getHistory(utrv);
  return history.length <= 0 ? '' : history[0].userId;
}

export function getLatestHistoryUser(
  utrv: { history?: MinimalValueHistory[] },
  users: { _id: string; name?: string; firstName?: string; surname?: string }[]
): {
  _id?: string;
  name?: string;
  firstName?: string;
  surname?: string;
} {
  const defaultName = { firstName: '-', surname: '', name: '-' };
  if (!Array.isArray(users)) {
    return defaultName;
  }

  const userId = getLastHistoryUserId(utrv);
  if (!userId) {
    return defaultName;
  }

  const user = users.find((u) => u._id === userId);

  const latestUser = user ? { ...user, name: user.firstName + ' ' + user.surname } : defaultName;
  return latestUser;
}

export function hasStakeholderAccess(stakeholders: any, userId: string | undefined) {
  if (!stakeholders || !userId) {
    return false;
  }

  return stakeholders.stakeholder.includes(userId) || stakeholders.verifier.includes(userId);
}

export const getNaType = ({
  status,
  value,
  valueData,
}: Partial<Pick<UniversalTrackerValuePlain, 'status' | 'value' | 'valueData'>>): string => {
  //Created items can't have a value set, so can't be N/A
  if (status === UpdateActions.ACTION.UTR.CREATED) {
    return '';
  }

  // Value is set, can't be N/A
  if (value !== undefined) {
    return '';
  }

  // value is empty and there is no valueData, must be N/A (legacy utrvs)
  if (!valueData) {
    return '';
  }

  //This property is a dead give-away, but is not guaranteed
  const { notApplicableType, table, data } = valueData;

  if (notApplicableType) {
    return notApplicableType;
  }

  // ValueData table/data are both empty
  const emptyTable = table === undefined || table.length === 0;
  return data === undefined && emptyTable ? NotApplicableTypes.na : '';
};

export const isNa = (utrv: Pick<UniversalTrackerValuePlain, 'status' | 'value' | 'valueData'>): boolean => {
  return getNaType(utrv) === NotApplicableTypes.na;
}

export const isNr = (utrv: Pick<UniversalTrackerValuePlain, 'status' | 'value' | 'valueData'>): boolean => {
  return getNaType(utrv) === NotApplicableTypes.nr;
}

export const isNaOrNr = (utrvLikeData: Pick<SurveyModelMinimalUtrv, 'status' | 'value' | 'valueData'>) => Boolean(getNaType(utrvLikeData));

export const isAssuredLocked = (utrv: Pick<UniversalTrackerValuePlain, 'assuranceStatus'>) => {
  return utrv.assuranceStatus === UtrvAssuranceStatus.Completed;
}

export const getStatusReadableText = (status: UtrvStatus) => {
  return StatusReadableText[status] ?? status;
}

export function validateMinMax(value: any, min?: number, max?: number) {
  if (!isNumeric(value)) {
    return { errored: true, message: 'Value must be numeric' };
  }
  if (isNumeric(min) && value < min) {
    return { errored: true, message: `Value must be greater than ${min}` };
  }
  if (isNumeric(max) && value > max) {
    return { errored: true, message: `Value must be less than ${max}` };
  }

  return { errored: false, message: '' };
}

export const getListValueDataData = ({ valueData }: Pick<ValueHistory, 'valueData'>) => {
  if (!valueData) {
    return {};
  }

  const { data, input } = valueData;
  if (input && input.data) {
    return input.data;
  }

  return data || {};
};

export const getTableInputData = ({ valueData }: Pick<UniversalTrackerValuePlain, 'valueData'>): TableValueDataData => {
  if (!valueData) {
    return [];
  }

  const { table, input } = valueData;
  if (input && input.table) {
    return input.table;
  }

  return table || [];
};

export const getInputValue = ({ value, valueData }: Pick<UniversalTrackerValuePlain, 'value' | 'valueData'>) => {
  if (!valueData) {
    return value;
  }

  return valueData.input?.value !== undefined ? valueData.input.value : value;
};

const getValueDataPropName = (valueType: UtrValueType) => valueType === UtrValueType.Table ? 'table' : 'data'

export const getValueData = (
  { valueData }: Pick<UniversalTrackerValuePlain, 'valueData'>,
  valueType: UtrValueType
) => {
  if (!valueData) {
    return {};
  }

  const propName = getValueDataPropName(valueType)
  return valueData.input?.[propName] ? valueData.input : valueData;
}

export const getValueDataProp = (
  valueData: ValueData | ValueData['input'] | undefined,
  valueType: UtrValueType
): ValueData['table'] | ValueData['data'] => {
  if (!valueData) {
    return {};
  }
  const propName = getValueDataPropName(valueType)
  if ('input' in valueData && valueData.input?.[propName]) {
    return valueData.input[propName]
  }
  return valueData[propName];
}

export const canExecuteAction = ({ status }: Pick<UniversalTrackerValuePlain, 'status'>, action: string, isVerifier?: boolean) => {
  const { UPDATE, VERIFY, REJECT } = UpdateActions.STATUS;
  const { CREATED, UPDATED, REJECTED, VERIFIED } = UpdateActions.ACTION.UTR

  switch (action) {
    case VERIFY:
      return [REJECTED, UPDATED].includes(status);
    case REJECT:
      return [VERIFIED, UPDATED].includes(status);
    case UPDATE:
      return isVerifier || [CREATED, REJECTED, UPDATED].includes(status);
    default:
      return false;
  }
}

type UtrUnitCheck = Pick<UniversalTrackerPlain, 'unit' | 'unitInput' | 'unitType'>;
export const getUnitCode = (
  utrv: Pick<SurveyModelMinimalUtrv, 'valueData' | 'status'>,
  utrObj: UniversalTracker | UtrUnitCheck,
  unitConfig?: Partial<UnitConfig>
): string => {
  const utr: UtrUnitCheck | undefined = 'universalTracker' in utrObj ? utrObj?.getRaw() : utrObj;

  if (utrv?.valueData?.input?.unit) {
    return utrv.valueData.input.unit;
  }

  if (utr?.unitInput) {
    return utr.unitInput;
  }

  const isCreated = utrv?.status === UtrvStatus.Created;
  const isNaNr = !!utrv?.valueData?.notApplicableType;
  const shouldUseUnitConfig = isCreated || isNaNr;
  if (shouldUseUnitConfig && utr?.unitType) {
    const unitType = utr.unitType as keyof UnitConfig;
    if (unitConfig?.[unitType]) {
      return unitConfig[unitType] ?? '';
    }
  }

  if (utr) {
    return utr.unit || '';
  }

  return '';
};

export const getUnitValue = (utrv: Pick<UniversalTrackerValuePlain, 'valueData' | 'value'>) => {
  return utrv?.valueData?.input?.value || utrv?.value;
};

type UtrNumberScaleCheck = Pick<UniversalTrackerPlain, 'numberScaleInput' | 'numberScale'>;
function getDefaultScale(utr: UtrNumberScaleCheck, defaultScale?: string) {
  return utr.numberScaleInput || utr.numberScale || defaultScale;
}

export const getNumberScale = (
  utrv: Pick<UniversalTrackerValuePlain, 'valueData' | 'numberScale' | 'status'>,
  utrObj: UniversalTracker | UtrNumberScaleCheck,
  unitConfig?: UnitConfig,
  defaultScale?: string
) => {
  const utr: UtrNumberScaleCheck | undefined = 'universalTracker' in utrObj ? utrObj?.getRaw() : utrObj;

  if (utrv) {
    if (utrv.valueData) {
      const { input } = utrv.valueData;
      if (input && input.numberScale) {
        return input.numberScale;
      }
    }
    if (utr?.numberScaleInput) {
      return utr.numberScaleInput;
    }
    if (utrv.numberScale) {
      return utrv.numberScale;
    }
  }

  if (utr) {
    if (utr.numberScale && utrv.status === UtrvStatus.Created && unitConfig?.numberScale) {
      return unitConfig.numberScale;
    }
    return getDefaultScale(utr, defaultScale);
  }

  return defaultScale;
};

export function getUnitDesc(unit: string | undefined, value: any, fallback = '') {
  if (!unit) {
    return fallback;
  }

  try {
    const desc = getUnitDescription(unit);
    return Number(value) === 1 ? desc.singular : desc.plural;
  } catch (e) {
    return fallback;
  }
}

export function getConvertedValue({
  value,
  unit,
  defaultUnit,
  numberScale,
  defaultNumberScale,
  isCurrency,
}: Partial<ConversionData>) {
  let convertedValue = value;
  if (numberScale && defaultNumberScale && numberScale !== defaultNumberScale) {
    convertedValue = convertValue(value, numberScale, defaultNumberScale);
  }
  if (!isCurrency && unit && defaultUnit && unit !== defaultUnit) {
    convertedValue = convertValue(convertedValue, unit, defaultUnit);
  }

  return convertedValue;
}

export function getSuffixDesc(
  {
    value,
    unit,
    defaultUnit,
    numberScale,
    defaultNumberScale,
    isCurrency,
  }: Pick<ConversionData, 'value' | 'unit' | 'defaultUnit' | 'numberScale' | 'defaultNumberScale' | 'isCurrency'>,
  isDefault = true
): string {
  let desc = '';
  if (numberScale && defaultNumberScale && numberScale !== defaultNumberScale) {
    desc = ' ' + getUnitDesc(isDefault ? defaultNumberScale : numberScale, value);
  }

  if (!isCurrency && unit && defaultUnit && unit !== defaultUnit) {
    desc = ' ' + getUnitDesc(isDefault ? defaultUnit : unit, value);
  }

  return desc;
}

function getConvertUnit(
  conversionData: ConversionData,
  className: string
): '' | JSX.Element {
  try {
    const desc = getSuffixDesc(conversionData);
    if (desc === '') {
      return '';
    }
    const convertedValue = getConvertedValue(conversionData);

    return (
      <React.Fragment>
        - Defaults to{' '}
        <NumberFormat
          className={`d-flex-inline strong ${className}`}
          minimumFractionDigits={0}
          value={convertedValue}
          decimalPlaces={3}
        />
        {desc}
      </React.Fragment>
    );
  } catch (e) {
    return '';
  }
}

export function renderNumericValue(
  conversionData: ConversionData,
  className: string = 'text-ThemeAccentMedium',
  showExtended: boolean = true
): JSX.Element {
  const { value, unit, numberScale, isCurrency, suffix } = conversionData;

  if (isNaN(value as number)) {
    return <span className={'badge bg-secondary'}>N/A</span>;
  }

  const unitDesc = getUnitDesc(isCurrency ? numberScale : unit, value, suffix);
  const prefix = isCurrency && unit ? `${unit} ` : '';

  const convertUnit = showExtended ? getConvertUnit(conversionData, className) : '';
  const unitText = (
    <>
      {unitDesc} {convertUnit}
    </>
  );

  const formattedNumber = (
    <NumberFormat
      className={`d-flex-inline align-top ${className}`}
      minimumFractionDigits={0}
      value={value}
      decimalPlaces={2}
    />
  );

  const tooltipText = (
    <>
      {prefix} {formattedNumber} {unitText}
    </>
  );

  return (
    <SimpleTooltip className='renderNumericValue' text={tooltipText}>
      <span className='w-50 prefix-and-value text-truncate'>
        <span className='prefix align-top'>{prefix}</span>
        {formattedNumber}
      </span>
      <span className='w-50 pl-2 suffix align-top text-truncate'>{unitText}</span>
    </SimpleTooltip>
  );
}

const baseConversion = (
  utrv: UniversalTrackerValue | Pick<SurveyModelMinimalUtrv, 'value' | 'valueData'> | ValueHistory,
  utr: UniversalTracker | UniversalTrackerPlain,
  { value, unit, numberScale }: Pick<ConversionData, 'value' | 'unit' | 'numberScale'>
): ConversionData => {

  const v = 'universalTrackerValue' in utrv ? utrv?.universalTrackerValue : utrv;
  const input = v.valueData?.input ?? {};
  const u = 'universalTracker' in utr ? utr?.getRaw() : utr;
  const suffix = 'getSuffix' in utr ? utr.getSuffix() : '';

  return {
    unit: unit || input.unit,
    defaultUnit: u.unit,
    numberScale: numberScale || input.numberScale,
    defaultNumberScale: u.numberScale,
    value,
    utr: u,
    isCurrency: u.unitType === UnitTypes.currency,
    suffix,
  };
};

export const conversionParams = baseConversion;

export const isUtrvAssuranceComplete = (status: undefined | UtrvAssuranceStatus) => {
  if (!status) {
    return false;
  }

  return [
    UtrvAssuranceStatus.Completed,
    UtrvAssuranceStatus.CompletedOpen,
  ].includes(status)

}

export const isUtrvAssuranceRejected = (status: undefined | UtrvAssuranceStatus) => {
  return status === UtrvAssuranceStatus.Rejected;
}

export const isUtrvAssuranceRestated = (status: undefined | UtrvAssuranceStatus) => {
  return status === UtrvAssuranceStatus.Restated;
}

export const getStatusIcon = (utrv: Pick<SurveyModelMinimalUtrv, 'assuranceStatus' | 'status'>) => {
  const { assuranceStatus, status } = utrv;
  if (isUtrvAssuranceComplete(assuranceStatus) || isUtrvAssuranceRejected(assuranceStatus)) {
    return 'fa-award';
  }
  if (assuranceStatus === UtrvAssuranceStatus.Restated) {
    return 'fa-rotate';
  }
  return getIconByStatus(status);
}

export const getIconByStatus = (status: UtrvStatus | string) => {
  switch (status) {
    case UtrvStatus.Created:
      return 'fa-user-clock text-ThemeHeadingLight';
    case UtrvStatus.Updated:
      return 'fa-user-edit text-ThemeAccentMedium';
    case UtrvStatus.Rejected:
      return 'fa-user-times text-ThemeDangerMedium';
    case UtrvStatus.Verified:
      return 'fa-user-check text-ThemeSuccessMedium';
    default:
      return 'fa-user';
  }
}

const restatedText = <>Restatement of previously assured data.<br />More info in the {QUESTION.SINGULAR}'s provenance section</>;

export const getStatusText = (utrv: Pick<SurveyModelMinimalUtrv, 'status' | 'assuranceStatus' | 'lastUpdated'>) => {
  if (isUtrvAssuranceComplete(utrv.assuranceStatus)) {
    return 'Assured';
  }

  if (isUtrvAssuranceRejected(utrv.assuranceStatus)) {
    return 'Assurance disputed';
  }

  const lastUpdated = formatDate(utrv.lastUpdated, DATE.LONG);

  switch (utrv.status) {
    case UtrvStatus.Created:
      return 'To be completed';
    case UtrvStatus.Updated:
      if (utrv.assuranceStatus === UtrvAssuranceStatus.Restated) {
        return <>Updated on {lastUpdated}<br />{restatedText}</>;
      }
      return 'Waiting verification';
    case UtrvStatus.Rejected:
      if (utrv.assuranceStatus === UtrvAssuranceStatus.Restated) {
        return <>Rejected on {lastUpdated}<br />{restatedText}</>;
      }
      return 'Rejected, waiting resubmission';
    case UtrvStatus.Verified:
      if (utrv.assuranceStatus === UtrvAssuranceStatus.Restated) {
        return <>Verified on {lastUpdated}<br />{restatedText}</>;
      }
      return 'Verified on ' + lastUpdated;
    default:
      return 'Unknown';
  }
}
